/**
 * Read-only Shop view for unauthenticated visitors.
 *
 * The real ShopPage component (`./ShopPage.tsx`) depends on
 * `useApp()` for cart/wishlist/offer actions. The landing page +
 * cold visitors live outside the AppContext provider, so we render
 * this lightweight variant instead. Buttons that need auth swap to
 * "Sign in to make an offer" — that's the entire gate. Browsing,
 * sorting, and the unlock framing all work the same.
 *
 * Data comes from the public `GET /api/products` endpoint
 * (api/src/index.ts:220 — "GETs are public").
 */
import React, { useEffect, useState, useMemo } from 'react';
import { productsAPI } from '../../services/api';
import { Product } from '../../context/AppContext';
import './ShopPage.css';
import '../../components/FeaturedSpotlightModal/FeaturedSpotlightModal.css';

interface GuestShopPageProps {
  onSignIn: () => void;
  onBackToLanding: () => void;
}

const GUEST_SPOTLIGHT_KEY = 'oj_guest_spotlight_seen';

const GuestShopPage: React.FC<GuestShopPageProps> = ({ onSignIn, onBackToLanding }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'hot' | 'newest' | 'lowest'>('hot');
  const [spotlight, setSpotlight] = useState<Product | null>(null);

  useEffect(() => {
    let cancelled = false;
    productsAPI.getAll()
      .then(({ products }) => {
        if (cancelled) return;
        const list = (products || []) as unknown as Product[];
        setProducts(list);
        // Surface the featured drop once per session so cold visitors
        // see the strongest piece + the unlock framing before they
        // even scroll. sessionStorage keeps this from being annoying
        // on repeat visits.
        let alreadySeen = false;
        try { alreadySeen = sessionStorage.getItem(GUEST_SPOTLIGHT_KEY) === '1'; } catch { /* ignore */ }
        if (!alreadySeen) {
          const featured = list.find(p => (p as any).featured);
          if (featured) setSpotlight(featured);
        }
      })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const closeSpotlight = () => {
    try { sessionStorage.setItem(GUEST_SPOTLIGHT_KEY, '1'); } catch { /* ignore */ }
    setSpotlight(null);
  };

  const liveItems = useMemo(() => {
    const list = [...products];
    const ts = (p: Product) => {
      const v = p.createdAt as any;
      return v instanceof Date ? v.getTime() : new Date(v).getTime();
    };
    switch (sortBy) {
      case 'newest':
        list.sort((a, b) => ts(b) - ts(a));
        break;
      case 'lowest':
        list.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      default:
        list.sort((a, b) => {
          const oa = a.offers?.length || 0;
          const ob = b.offers?.length || 0;
          if (ob !== oa) return ob - oa;
          return ts(b) - ts(a);
        });
    }
    return list;
  }, [products, sortBy]);

  return (
    <div className="shop-page guest-shop-page">
      <div className="guest-shop-topbar">
        <button type="button" className="guest-shop-back" onClick={onBackToLanding} aria-label="Back to home">
          ← Home
        </button>
        <button type="button" className="guest-shop-signin" onClick={onSignIn}>
          Sign in
        </button>
      </div>

      <div className="shop-hero">
        <span className="shop-eyebrow">1-of-1 Auctions</span>
        <h1>Jalen's Closet</h1>
        <p>Real pieces from my life. Submit your offer, win the bid, take it home.</p>
        <div className="shop-hero-pills">
          <span className="shop-pill">✨ One of a kind</span>
          <span className="shop-pill">📸 + Exclusive photos with purchase</span>
          <span className="shop-pill">💸 PayPal secured</span>
        </div>
      </div>

      <div className="shop-controls">
        <div className="sort-tabs" role="tablist">
          {([
            { id: 'hot', label: '🔥 Hot' },
            { id: 'newest', label: 'Newest' },
            { id: 'lowest', label: 'Lowest start' },
          ] as const).map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={sortBy === t.id}
              className={`sort-tab ${sortBy === t.id ? 'active' : ''}`}
              onClick={() => setSortBy(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="product-count">
          {liveItems.length} {liveItems.length === 1 ? 'piece' : 'pieces'} live
        </span>
      </div>

      {spotlight && (
        <div
          className="spotlight-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Featured: ${spotlight.name}`}
          onClick={(e) => { if (e.target === e.currentTarget) closeSpotlight(); }}
        >
          <div className="spotlight-card animate-spotlight">
            <button
              type="button"
              className="spotlight-close"
              onClick={closeSpotlight}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <div className="spotlight-hero">
              {spotlight.images?.[0] ? (
                <img
                  src={spotlight.images[0]}
                  alt={spotlight.name}
                  className="spotlight-hero-img"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div className="spotlight-hero-placeholder" aria-hidden="true">🧥</div>
              )}
              <span className="spotlight-pill">✨ Just dropped</span>
            </div>
            <div className="spotlight-body">
              <h3 className="spotlight-name">{spotlight.name}</h3>
              {spotlight.description && (
                <p className="spotlight-description">{spotlight.description}</p>
              )}
              <div className="spotlight-price-row">
                <span className="spotlight-price">${(spotlight.salePrice || spotlight.price).toFixed(2)}</span>
              </div>
              <div className="spotlight-actions">
                <button
                  type="button"
                  className="spotlight-btn spotlight-btn-primary"
                  onClick={() => { closeSpotlight(); onSignIn(); }}
                >
                  Sign in to make an offer
                </button>
                <button
                  type="button"
                  className="spotlight-btn spotlight-btn-ghost"
                  onClick={closeSpotlight}
                >
                  Keep browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="products-section">
        {loading ? (
          <div className="shop-empty">
            <span className="shop-empty-icon">⏳</span>
            <h3>Loading the closet…</h3>
          </div>
        ) : liveItems.length === 0 ? (
          <div className="shop-empty">
            <span className="shop-empty-icon">🧥</span>
            <h3>Nothing live right now</h3>
            <p>New pieces drop here as soon as Jalen lists them. Sign in to get notified.</p>
            <button type="button" className="guest-shop-signin-large" onClick={onSignIn}>
              Sign in to follow drops
            </button>
          </div>
        ) : (
          <div className="products-grid">
            {liveItems.map(p => {
              const hero = p.images?.[0];
              const price = (p.salePrice || p.price).toFixed(0);
              const offerCount = p.offers?.length || 0;
              return (
                <div key={p.id} className="product-card animate-scale-in" onClick={onSignIn} role="button" tabIndex={0}>
                  <div className="product-image">
                    {hero ? (
                      <img src={hero} alt={p.name} loading="lazy" />
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', fontSize: 48, opacity: 0.4 }}>🧥</div>
                    )}
                    <div className="card-badges">
                      <span className="unique-badge">1 of 1</span>
                    </div>
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{p.name}</h3>
                    <div className="product-meta">
                      {offerCount > 0 ? (
                        <span className="offer-count">{offerCount} offer{offerCount !== 1 ? 's' : ''}</span>
                      ) : (
                        <span className="no-offers">Be first to offer</span>
                      )}
                    </div>
                    <span className="product-unlock-badge">✨ + Exclusive photos with purchase</span>
                    <div className="product-price">
                      <span className="starting-bid-label">Starting bid</span>
                      <span className="starting-bid-amount">${price}</span>
                    </div>
                    <div className="product-cta-row">
                      <button
                        type="button"
                        className="add-to-cart-btn"
                        onClick={(e) => { e.stopPropagation(); onSignIn(); }}
                      >
                        Sign in to make an offer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestShopPage;
